import { RING_COMPACT, RING_LARGE, ringPath, trackPath, type Ring } from '@/components/gauge'
import { DASH } from '@/ui/format'

// 원형 게이지 하나 — 할당량 대비 사용률(0~1). 큰 것은 가운데에 퍼센트, 아래에 라벨과 `쓴 양 / 총량`.
// 값이 없으면 빈 고리와 `—`. 색은 차트 토큰이고 임계값으로 바꾸지 않는다: 얼마나 쓰는지는 사실이지 판정이 아니다.
// compact 는 목록의 것이다: 지름 48px 고리 하나에 글자가 없고, 이름은 aria-label 에만 있다(`CPU 0.6/2 코어`). 숫자는 호출자가 아래에 둔다.
// 고리는 손으로 그린 SVG 다 — 목록의 줄마다 서는 것이라 차트 라이브러리의 컨테이너·크기 관찰자·인스턴스마다의 `<style>` 이
// 줄 수만큼 생겼다. 모양의 수는 `frontend/src/components/gauge.ts` 에 있다. 이름은 감싼 div 의 것이고 svg 는 접근성 트리에서 빠진다
// — Tab 으로 멈추는 자리도 아니다.
export function Gauge({ label, fraction, text, color = 'var(--chart-1)', compact = false }: { label: string; fraction: number | null; text: string; color?: string; compact?: boolean }) {
  const pct = fraction === null ? null : Math.round(Math.min(1, Math.max(0, fraction)) * 100)
  if (compact) {
    return (
      <div className="size-12 shrink-0" role="img" aria-label={`${label} ${text}`}>
        {/* 궤도는 `--border` 다: `--muted` 는 행의 hover 바탕(`--surface-2`)과 같은 색이라 빈 고리가 hover 때 사라진다. */}
        <RingSvg ring={RING_COMPACT} pct={pct} color={color} track="fill-border" />
      </div>
    )
  }
  const head = pct === null ? DASH : `${pct}%`
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="relative size-32" role="img" aria-label={`${label} ${head}`}>
        <RingSvg ring={RING_LARGE} pct={pct} color={color} track="fill-muted" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[17px] font-medium tabular-nums">{head}</div>
      </div>
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className="text-[12px] tabular-nums">{text}</span>
    </div>
  )
}

/** 궤도 한 바퀴와, 값이 있으면 그 위의 막대. 0% 는 막대가 없다. */
function RingSvg({ ring, pct, color, track }: { ring: Ring; pct: number | null; color: string; track: 'fill-border' | 'fill-muted' }) {
  const bar = pct === null ? null : ringPath(ring, pct)
  return (
    <svg viewBox={`0 0 ${ring.box} ${ring.box}`} width={ring.box} height={ring.box} className="block" aria-hidden="true" focusable="false">
      <path d={trackPath(ring)} className={track} />
      {bar && <path d={bar} fill={color} />}
    </svg>
  )
}
